import * as SVG from 'svg.js';
import { SALESFORCE_SANS } from 'lib/svgs';
import { AnalyticsChartConfig, AnalyticsBarData, AnalyticsChartConfigs } from 'lib/types';

type CloudNames = 'none' | 'marketing' | 'mulesoft' | 'retail' | 'sales' | 'service' | 'community';
type CloudColor = {
	total: string
	unique: string
};

const CLOUD_COLORS: Record<CloudNames, CloudColor> = {
	none: {total: '#C4C6CA', unique: '#8A8683'},
	marketing: {total: '#FDBE87', unique: '#EA7600'},
	mulesoft: {total: '#88b1e2', unique: '#032e61'},
	retail: {total: '#B3D7A9', unique: '#416437'},
	sales: {total: '#86C8BC', unique: '#00857D'},
	service: {total: '#C996B6', unique: '#812244'},
	community: {total: '#EBBC48', unique: '#e7a536ff'}
};

export class Chart {
	analytics: AnalyticsBarData[];
	config: AnalyticsChartConfig;
	maxTotalCount: number;
	size: {h: number, w: number};
	svg: SVG.Doc;

	constructor (el: HTMLElement, analytics: AnalyticsChartConfigs) {
		this.config = analytics.chart;
		this.analytics = analytics.data;
		this.svg = SVG(el).size(this.config.width, this.config.height);
		this.svg.defs().svg(SALESFORCE_SANS);
		const box = this.svg.rbox();
		this.size = {h: box.h, w: box.w};
	}

	renderLegend () {
		const legend = this.svg.group();
		const circleSize = 8;
		const clouds = Array.from(new Set(this.analytics.map((bar) => bar.cloud)));

		clouds.forEach((name, i) => {
			legend.circle(circleSize).fill(CLOUD_COLORS[name].total).translate(i * (circleSize + 2), 6.5);
		});

		legend
			.text('Total Qualified Records')
			.fill('#8A8683')
			.font({size: 11})
			.translate((clouds.length + 1.5) * circleSize, 0);

		clouds.forEach((name, i) => {
			legend.circle(circleSize).fill(CLOUD_COLORS[name].unique).translate(i * (circleSize + 2), 26.5);
		});

		legend
			.text(this.config.uniquesLabel || 'Unique Records')
			.fill('#8A8683')
			.font({size: 11})
			.translate((clouds.length + 1.5) * circleSize, 20);
	}

	renderAxis (axes: SVG.G, text: string, xValue: number, labelOffset: number) {
		const axesStartY = this.config.topPadding - 30;
		const axesHeight = this.analytics.length * (this.config.barHeight + this.config.barPadding) + this.config.topPadding;

		axes
			.text(text)
			.fill(this.config.axesColor)
			.font({size: 11})
			.translate(xValue - labelOffset, axesStartY);

		axes
			.line(xValue, axesStartY + 20, xValue, axesHeight)
			.stroke({width: 1, color: this.config.axesColor});
	}

	renderAxes () {
		// tslint:disable-next-line:no-console
		const chartAreaWidth = this.size.w - this.config.labelWidth;
		const axes = this.svg.group();
		const axisSpacing = chartAreaWidth / 2;
		let axisLabel1 = (this.maxTotalCount * 1.2) / 2;
		let axisLabel2 = (this.maxTotalCount * 1.2);

		// If the chart uses a log scale, get the real total for the right, and do the inverse ln to find the middle
		if (this.config.logScale) {
			const realMax = Math.max(...this.analytics.map(b => parseFloat(b.totalCount)));
			const realMiddle = 0.6122591943957969; // Accounts for the bars being slightly shorter from the label padding below
			axisLabel1 = Math.pow(Math.E, this.maxTotalCount * realMiddle); // e^x is the inverse of ln, aka Math.log
			axisLabel2 = (realMax * 1.2);
		}
		// Adjusts offset depending on magnitude of the value
		const rightLabelOffset = axisLabel2 < 10 ? 15 : axisLabel2 < 100 ? 20 : 25;

		this.renderAxis(axes, '0', this.config.labelWidth, 3);
		this.renderAxis(
			axes, 
			`${axisLabel1.toFixed(0)}${this.config.axesLabelSuffix}`,
			this.config.labelWidth + axisSpacing, 10
		);
		this.renderAxis(
			axes,
			`${axisLabel2.toFixed(0)}${this.config.axesLabelSuffix}`,
			this.config.labelWidth + axisSpacing * 2, rightLabelOffset
		);
	}

	renderBarLabel (row: SVG.G, bar: AnalyticsBarData) {
		const label = row.text(bar.name).fill('#3E3E3C').font({size: 13});
		label.translate(this.config.labelWidth - label.length() - 10, 0);
	}

	renderBar = (bar: AnalyticsBarData, i: number, bars: AnalyticsBarData[]) => {
		const barValueLabelWidth = 24;
		const chartAreaWidth = this.size.w - this.config.labelWidth;
		let totalCount = parseFloat(bar.totalCount);
		let uniqueCount = parseFloat(bar.uniqueCount);

		if (this.config.logScale) {
			totalCount = Math.log(totalCount);
			uniqueCount = Math.log(uniqueCount);
		}

		const row = this.svg.group();
		const rowY = (this.config.barPadding + this.config.barHeight) * i;
		const totalBarWidth = Math.round(chartAreaWidth * (totalCount / this.maxTotalCount)) - barValueLabelWidth;
		const totalBarWithLabelWidth = totalBarWidth + this.config.labelWidth + this.config.barValueX;
		const uniqueBarWidth = Math.round(chartAreaWidth * (uniqueCount / this.maxTotalCount)) - barValueLabelWidth;
		const uniqueBarWithLabelWidth = uniqueBarWidth + this.config.labelWidth + this.config.barValueX;
		let barY = 0;

		row.translate(0, rowY + this.config.topPadding);

		if (bar.separator) {
			const offset = -5;
			row
				.line(0, offset, this.size.w - 14, offset)
				.stroke({width: 1, color: this.config.axesColor})
				.attr({'stroke-dasharray': '5,5'});
		}

		this.renderBarLabel(row, bar);
		// Total bar & label
		row
			.rect(0, this.config.barHeight)
			.fill(CLOUD_COLORS[bar.cloud].total)
			.translate(this.config.labelWidth, barY)
			.animate(250, '<', 30 * i).attr({width: totalBarWidth});
		row
			.text(bar.totalCount)
			.font({size: 8})
			.fill('#3E3E3C')
			.translate(totalBarWithLabelWidth, this.config.barValueY);
		// Uniques bar & label
		if (parseFloat(bar.uniqueCount) < parseFloat(bar.totalCount)) {
			row
				.rect(0, this.config.barHeight)
				.fill(CLOUD_COLORS[bar.cloud].unique)
				.translate(this.config.labelWidth, barY)
				.animate(this.config.uniquesAnimateDelay, '>', 75 * i).attr({width: uniqueBarWidth});
			row
				.text(bar.uniqueCount)
				.font({size: 8})
				.fill('#FFF')
				.translate(uniqueBarWithLabelWidth, this.config.barValueY);
		}
	}

	render () {
		this.maxTotalCount = Math.max(...this.analytics.map(b => parseFloat(b.totalCount)));
		if (this.config.logScale) {
			this.maxTotalCount = Math.log(this.maxTotalCount);
		}
		this.renderLegend();
		this.renderAxes();
		this.analytics.forEach(this.renderBar);
	}
}


// WEBPACK FOOTER //
// ./src/Dashboard/AnalyticsChart.ts